import { BiddingBidSendStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  IconButton,
  MenuRoot,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuTrigger as RawMenuTrigger,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaEllipsisRegular } from '@enechain/ecloud-icons'

import { BidMemoCell } from '~/components/bidding/BidMemoCell'
import { SendErrorReasonTooltip } from '~/components/bidding/SendErrorReasonTooltip'
import { Badge } from '~/components/common/Badge'
import type { EnumBadgeSpec } from '~/components/common/EnumStatusBadge'
import {
  TABLE_HEADER_BG,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import {
  STICKY_ACTION_HOVER_OVERLAY,
  stickyActionCellStyle,
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import { TableMessageRow } from '~/components/common/TableMessageRow'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { iconSize } from '~/constants/iconSize'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { convertAreaLabel } from '~/models/area'
import { isDraftWithSendErrorReason } from '~/models/bidDraftReturnReason'
import { BlockLegKindBadge } from '~/pages/SpotBidding/components/BlockLegKindBadge'
import { WithdrawStateBadge } from '~/pages/SpotBidding/components/WithdrawStateBadge'
import { BID_STATUS_CATEGORY_LABEL_KEY } from '~/pages/SpotBidding/models/bidStatusCategory'
import { listedWithdrawState } from '~/pages/SpotBidding/models/bidWithdrawState'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import {
  type BlockBidStatusCategory,
  deriveBlockBidStatusCategory,
} from '~/pages/SpotBidding/models/blockBidStatusCategory'
import { canWithdrawBlockBidGroup } from '~/pages/SpotBidding/models/blockBidWithdrawAction'
import { isNonExpiredDraftBlockRow } from '~/pages/SpotBidding/models/blockDraftGroup'
import { formatBlockVolumeLabel } from '~/pages/SpotBidding/models/blockVolumeLabel'
import { formatCreatedLabel } from '~/pages/SpotBidding/models/createdLabel'
import { canReturnToDraft } from '~/pages/SpotBidding/models/returnToDraftAction'

import { NeedsReviewChip } from './NeedsReviewChip'

/**
 * chakra v3 の `MenuTriggerProps` / `MenuContentProps` / `MenuItemProps` は本プロジェクトの
 * 型解決 (ecloud 0.14 / chakra 3.29 の d.ts 解決問題) で `HTMLChakraProps` が collapse し
 * `children` / `value` / `onSelect` / style props が欠落する。 v3 本来の prop シグネチャ
 * (`HTMLChakraProps<element>` + ark-ui 固有 prop) へ cast して復元する (= SpotBidTable と同形)。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    disabled?: boolean
    onSelect?: () => void
  }
>

type Props = {
  rows: BlockBidRow[]
  /** group 単位の編集トリガー (= 親行のみ表示、 groupId を渡す)。 */
  onEditGroup?: (groupId: string) => void
  /** group 単位の削除トリガー (= 親行のみ表示、 groupId を渡す)。 */
  onDeleteGroup?: (groupId: string) => void
  /**
   * 入札中の group の「取下」 (AC-BSP-057、 親行のみ表示)。 出すかどうかの判定は
   * [[canWithdrawBlockBidGroup]] に閉じる。
   */
  onWithdrawGroup?: (groupId: string) => void
  /**
   * 送信中の group の「下書きに戻す」 (AC-BSP-117 / AC-BSP-129、 取下と同じ親行に出す)。 出すかどうかの
   * 判定は [[canReturnToDraft]] に閉じる。
   */
  onReturnGroupToDraft?: (groupId: string) => void
  /** 確定済ワークフロー経由で画面が read-only か (AC-BSP-058)。 */
  isWorkflowReadonly?: boolean
  /**
   * ステータス列の 11 区分導出 ([[deriveBlockBidStatusCategory]], AC-BSP-049) に渡す
   * 入札締切通過フラグ。 渡し忘れると締切後の行を取り違えるため必須にしている。
   */
  isPastDeadline: boolean
  /**
   * 行クリックで group を開く (design `spot.html`「入札一覧テーブル: 行クリック →
   * 編集モーダル起動」/ AC-BSP-001a「通常入札・ブロック入札の両一覧に適用する」。
   * `SpotBidTable` の `onOpenBid` と同じ規約)。 親行・子行どちらをクリックしても
   * 同じ group が開く (= `row.groupId` を渡す)。 編集できる group かどうかの判断は
   * 呼び出し側 (親 page) が group の承認状態で行う。
   */
  onOpenGroup?: (groupId: string) => void
}

/**
 * ステータス列の 11 区分 (AC-BSP-049) → 状態バッジの色。 区分名の i18n key は
 * CSV のステータス列と同じ [[BID_STATUS_CATEGORY_LABEL_KEY]] から引く。
 *
 * 色は通常入札のステータス列 [[SPOT_BID_STATUS_CATEGORY_BADGE]] と同じ写像を使う
 * (部分約定が無いだけで語彙・色は共通):
 *   全約定 → positive / 約定待ち・承認待ち → warning /
 *   送信中・入札中・予約中 → info / 下書き・未約定・取消 → neutral /
 *   差戻し・失効 → negative
 *
 * [[BlockBidStatusCategory]] の網羅 Record なので、 区分が増えるとここが型
 * エラーになり追従漏れを検出できる。
 */
const BLOCK_BID_STATUS_CATEGORY_BADGE_STATUS: Record<
  BlockBidStatusCategory,
  EnumBadgeSpec['status']
> = {
  reserved: 'info',
  draft: 'neutral',
  pendingApproval: 'warning',
  rejected: 'negative',
  sending: 'info',
  active: 'info',
  awaitingMatch: 'warning',
  allMatched: 'positive',
  unmatched: 'neutral',
  cancelled: 'neutral',
  expired: 'negative',
}

/** 列数 (= 空行の `colSpan`)。 */
const BLOCK_BID_COLUMN_COUNT = 13

/**
 * メモ 2 欄 (JEPX自由記述欄 / メモ) の列幅。 通常入札 (SpotBidTable) の同項目と同じ
 * 180px (design `.col-memo` の max-width 180px に合わせ、 min と max を同値にする。
 * SpotBidTable の `COL_MIN_WIDTH.memoJepx` / `.memoFree` と同じ理由)。
 */
const MEMO_COL_WIDTH = '180px'

/** 作成列の幅。 通常入札 (SpotBidTable) の `COL_MIN_WIDTH.createdAt` と同じ design の実測値。 */
const CREATED_COL_MIN_WIDTH = '219px'

/**
 * group 単位の編集/削除アクションを表示してよい行か (= issue #2090)。
 *
 * - DRAFT 以外 (PENDING/APPROVED/REJECTED/SUBMITTED) は backend が FailedPrecondition を
 *   返すため UI で先に gate する。
 * - 入札締切まで提出せず失効にした札は承認ステータスが DRAFT に戻るが、 編集も削除も
 *   意味を持たないので出さない (AC-BSP-099)。
 * - LINK/LOOP の 2 leg group は子行 (LINK-C / LOOP-B) にアクションを出さない
 *   (= 編集/削除は group 単位のため、 親行 1 箇所にのみ集約する)。
 */
const canManageGroup = (row: BlockBidRow): boolean =>
  isNonExpiredDraftBlockRow(row) && row.isChild !== true

/**
 * ステータスのバッジに送信失敗の理由を hover で添える行か。 一覧に送信の列を持たない
 * (#7520) ので、 送信軸が失敗の札 (区分が送信中・失効のどちらでも) の理由もここで出す
 * (AC-BSP-011)。 再検証の違反で下書きに戻した札 ([[isDraftWithSendErrorReason]]、
 * AC-BSP-117) も同じバッジに理由を出す。
 */
const hasSendErrorReason = (row: BlockBidRow): boolean =>
  row.sendStatus === BiddingBidSendStatus.SEND_FAILED ||
  row.sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL ||
  isDraftWithSendErrorReason(row)

/**
 * ブロック入札一覧テーブル。
 *
 * 列は spec AC-BSP-028 の列挙 (design `design/bidding/spot.html` `#block-bid-table` の thead も同じ)。
 * ステータスだけは画面共通の規約 (2026-08-27 デザイナー判断) で先頭に置く。
 * 送信の独立列・ブロック ID・名称は持たない (送信はステータス列の統合区分で表し、
 * 送信失敗の理由はそのバッジの hover で出す。 AC-BSP-047 / 049 / 011)。
 * 編集/削除アクションは group 単位のため親行にのみ表示する (`canManageGroup` 参照)。
 */
export const BlockBidTable = ({
  rows,
  onEditGroup,
  onDeleteGroup,
  onWithdrawGroup,
  onReturnGroupToDraft,
  isWorkflowReadonly = false,
  isPastDeadline,
  onOpenGroup,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveMemberDisplayName = useMemberDisplayName()
  // 操作列 (⋯) を右端に固定する (列が多く横スクロールが常態。 共通 hook / UI 辞書 table.md §1)。
  const { wrapRef, stickyShadow } = useStickyRightShadow([rows])
  return (
    <Box
      ref={wrapRef}
      overflowX="auto"
      // `TableMessageRow` の内側 Box は `100cqi` (= 見えている幅) を参照して
      // 中央寄せするので、 スクロールする器を inline-size コンテナにしておく
      // (= `MasterDataTable` の wrap と同じ前提。 無いと cqi がこの器を参照できず、
      //  横スクロールする狭い表示幅で 0 件メッセージが可視領域の中央から外れる)。
      // block 軸は含めない (`container-type: size` は過去に wrap 0px 化を起こした)。
      style={{ containerType: 'inline-size' }}
    >
      <TableRoot
        data-row-hover={onOpenGroup !== undefined ? '' : undefined}
        data-zebra
      >
        <TableHeader
          // ecloud 0.14.2 の `TableColumnHeader` は素の chakra 既定のままで éclair の
          // TH 規定 (地 = Surface.tableHeader / 文字 = Text.midEmphasis / 600 /
          // padding 10px 12px) を持たない。 8080 の `.ecl-table th` と同じ見た目を当てる
          // (デザイナー指摘 2026-08-27「8080同様、DS Table 適用 (TH)」を、 取り残されていた
          // ブロック入札の 2 表へ展開: 2026-08-31「ブロック入札 > 入札一覧の TH は DS 準拠」)。
          // style は共通部品の single source (`TABLE_HEADER_CELL_STYLE`) を使う
          // (= 通常入札 SpotBidTable と同じ)。
          // 以前は `fontSize: 'md'` / `fontWeight: 'semibold'` だけを当てており、
          // 地色・文字色・padding が抜けて同画面の他 3 表と見出しが揃っていなかった。
          // `textAlign` / `width` は列ごとに違うので css には入れない (各 TH の prop が正)。
          css={{ '& th': TABLE_HEADER_CELL_STYLE }}
        >
          <TableRow>
            {/* ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
                行の状態は他の属性を読む前に分かる必要があるため。 */}
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.status')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.kind')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.area')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.side')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.column.komaRange')}
            </TableColumnHeader>
            {/*
              数量はコマ別 MW (spec AC-051 / AC-BSP-053)。 proto field / DB 列名が
              `volume_mwh` なのは旧 spec 時代の歴史的経緯で、 格納値は MW (issue #4285)。
              ブロック入札はコマ毎に異なる量を取り得る (AC-BSP-030a) ため、 全コマ同一なら
              単一値、 異なる場合は「最小〜最大 (可変)」を出す ([[formatBlockVolumeLabel]])。
            */}
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.column.volumeMw')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.column.unitPriceYenPerKwh')}
            </TableColumnHeader>
            {/* 受渡契約コード (AC-BSP-028)。 発注主体区分×入札エリアから広域マスタを
                解決して leg に保存した読み取り専用の応答値 (#9613)。 保存前の旧い leg は
                値を持たず「—」表示。 */}
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.deliveryContractCode')}
            </TableColumnHeader>
            {/* JEPX自由記述欄 / メモ (AC-BSP-028)。 長文は省略表示 + hover で全文。 */}
            <TableColumnHeader minWidth={MEMO_COL_WIDTH}>
              {t('page:biddingSpot.column.memoJepx')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={MEMO_COL_WIDTH}>
              {t('page:biddingSpot.column.memoFree')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.column.bidNo')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={CREATED_COL_MIN_WIDTH}>
              {t('page:biddingSpot.column.createdAt')}
            </TableColumnHeader>
            <TableColumnHeader
              width="80px"
              textAlign="center"
              aria-label={t('common:table.actionsLabel')}
              data-sticky-shadow={stickyShadow}
              style={{
                ...TABLE_HEADER_CELL_STYLE,
                // th は個別に sticky top (TABLE_HEADER_CELL_STYLE)。 右端固定と交わる角は z=3。
                ...stickyActionHeaderStyle(TABLE_HEADER_BG),
              }}
            />
          </TableRow>
        </TableHeader>
        {/* TD は design `.ecl-table td` の 6px 10px (UI 辞書 table.md §0) */}
        <TableBody>
          {rows.length === 0 && (
            <TableMessageRow
              colSpan={BLOCK_BID_COLUMN_COUNT}
              message={t('page:biddingBlock.list.empty')}
            />
          )}
          {rows.map((row, rowIndex) => {
            const statusCategory = deriveBlockBidStatusCategory(
              row,
              isPastDeadline,
            )
            const statusBadgeSpec: EnumBadgeSpec = {
              status: BLOCK_BID_STATUS_CATEGORY_BADGE_STATUS[statusCategory],
              labelKey: BID_STATUS_CATEGORY_LABEL_KEY[statusCategory],
            }
            // 列の多い表で列幅が詰まっても Badge が 2 行に折れないよう nowrap
            // (DS Badge は高さ 24px 固定。 UI 辞書 sweep の共通注意)。
            const statusBadge = (
              <Badge whiteSpace="nowrap" status={statusBadgeSpec.status}>
                {t(statusBadgeSpec.labelKey)}
              </Badge>
            )
            // 要確認の印はグループの値で両 Leg の行が持つので、 取下失敗を隠すのも両 Leg (AC-BSP-125)。
            const withdrawState = listedWithdrawState(
              row.withdrawState,
              row.needsReview,
            )
            // LINK / LOOP は 2 本をまとめて取り下げるので親行にだけ出す (AC-BSP-057)。
            const createdLabel = formatCreatedLabel(
              resolveMemberDisplayName(row.createdBy),
              row.createdAt,
            )
            const canWithdrawRow =
              row.isChild !== true &&
              onWithdrawGroup !== undefined &&
              canWithdrawBlockBidGroup({
                group: row,
                isBeforeDeadline: !isPastDeadline,
                isWorkflowReadonly,
              })
            const canReturnRowToDraft =
              row.isChild !== true &&
              onReturnGroupToDraft !== undefined &&
              canReturnToDraft({
                target: row,
                isBeforeDeadline: !isPastDeadline,
                isWorkflowReadonly,
              })
            return (
              <TableRow
                key={row.id}
                _hover={
                  onOpenGroup !== undefined
                    ? STICKY_ACTION_HOVER_OVERLAY
                    : undefined
                }
                // 行 hover の地は操作列にも重ねる (hover の地は表の recipe `data-row-hover`)。
                cursor={onOpenGroup !== undefined ? 'pointer' : undefined}
                onClick={
                  onOpenGroup !== undefined
                    ? (event) => {
                        // 行内の操作 (⋯ メニュー・ボタン) のクリックでは開かない
                        // (SpotBidTable と同じ理由: これが無いと「削除」を押した
                        // 瞬間に編集/詳細モーダルも開いてしまう)。
                        if (
                          event.target instanceof Element &&
                          event.target.closest(
                            'button, a, [role="menuitem"]',
                          ) !== null
                        ) {
                          return
                        }
                        onOpenGroup(row.groupId)
                      }
                    : undefined
                }
              >
                {/* 表の行の左に太い border (ペア識別のバー) を入れないのは dev 全体の方針
                    (2026-09-28 デザイナー判断 #8414)。 種別 (リンク親/子・ループ A/B) は
                    種別列の BlockLegKindBadge が示す。 */}
                <TableCell textAlign="center">
                  {hasSendErrorReason(row) ? (
                    <SendErrorReasonTooltip
                      errorCategory={row.sendErrorCategory}
                      errorDetail={row.sendErrorDetail}
                    >
                      {statusBadge}
                    </SendErrorReasonTooltip>
                  ) : (
                    statusBadge
                  )}
                  {row.needsReview && !row.isChild && <NeedsReviewChip />}
                  {withdrawState !== undefined && (
                    <WithdrawStateBadge
                      marginLeft={1}
                      isPastDeadline={isPastDeadline}
                      state={withdrawState}
                    />
                  )}
                </TableCell>
                {/* 値が短い列 (種別・エリア・売/買・対象コマ範囲・数量・単価・受渡契約コード・入札番号) は
                    折り返さない (enechain/ebalance#11135 の Des 判断。 design `spot.html` の種別列と同じ)。
                    表は横にスクロールするので、 列を詰めて 1 行を 2〜4 段に折るより幅を取る。
                    メモ 2 列は省略表示 + hover で全文 (AC-BSP-028) のまま */}
                <TableCell textAlign="center" whiteSpace="nowrap">
                  <BlockLegKindBadge kind={row.kind} />
                </TableCell>
                <TableCell textAlign="center" whiteSpace="nowrap">
                  {convertAreaLabel(row.areaCode)}
                </TableCell>
                <TableCell textAlign="center" whiteSpace="nowrap">
                  <TradeSideLabel side={row.direction} />
                </TableCell>
                <TableCell
                  whiteSpace="nowrap"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {row.komaRangeLabel}
                </TableCell>
                <TableCell
                  textAlign="right"
                  whiteSpace="nowrap"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatBlockVolumeLabel(row.slotVolumes, row.volumeMwh)}
                </TableCell>
                <TableCell
                  textAlign="right"
                  whiteSpace="nowrap"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {row.priceYenPerKwh.toFixed(2)}
                </TableCell>
                <TableCell
                  textAlign="center"
                  whiteSpace="nowrap"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {row.deliveryContractCode !== undefined &&
                  row.deliveryContractCode !== ''
                    ? row.deliveryContractCode
                    : '—'}
                </TableCell>
                <BidMemoCell width={MEMO_COL_WIDTH} value={row.memoJepx} />
                <BidMemoCell width={MEMO_COL_WIDTH} value={row.memoFree} />
                <TableCell
                  textAlign="end"
                  whiteSpace="nowrap"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {row.bidNo !== undefined && row.bidNo !== ''
                    ? row.bidNo
                    : '—'}
                </TableCell>
                {/* 作成列は通常入札 (SpotBidTable) と同じ見た目 (8080 `td.col-audit`)。 */}
                <TableCell
                  minWidth={CREATED_COL_MIN_WIDTH}
                  maxWidth="220px"
                  color="Text.midEmphasis"
                  fontSize="sm"
                  css={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title={createdLabel}
                >
                  {createdLabel}
                </TableCell>
                <TableCell
                  textAlign="center"
                  data-sticky-action
                  data-sticky-shadow={stickyShadow}
                  style={stickyActionCellStyle(rowIndex)}
                >
                  {/* ⋯ メニュー。 編集できる行 (canManageGroup) は編集/削除、
                      それ以外は「詳細」 (= 表示専用で開く) を出す。 表の行は tab で
                      focus できないため、 行クリックだけではキーボードで開けない。
                      操作列の ⋯ が「focus できて名前を持つ同等の手段」になる
                      (WCAG 2.1.1、 SpotBidTable と同じ理由)。
                      DRAFT の子行 (LINK-C/LOOP-B) は canManageGroup が false (削除は
                      親行に集約) だが、 onOpenGroup は group の DRAFT 判定で開くため
                      実際は編集可能なモーダルが開く (index.tsx の readOnly は row 単位
                      でなく group 単位)。 ラベルを「詳細」のままにすると表示専用だと
                      誤認させるため、 DRAFT の子行だけ「編集」に言い換える
                      (削除ボタンは出さない = #2090 の集約方針は保持、 codex-review
                      指摘 (PR #8012) に対する自己レビューでの追加修正)。 */}
                  {(canManageGroup(row) ||
                    canWithdrawRow ||
                    canReturnRowToDraft ||
                    onOpenGroup !== undefined) && (
                    <MenuRoot>
                      <MenuTrigger aria-label={t('common:table.action.menu')}>
                        <Tooltip content={t('common:table.action.menu')}>
                          <IconButton
                            as="span"
                            aria-label={t('common:table.action.menu')}
                            size="sm"
                            variant="ghost"
                          >
                            {/* 行の ⋯ は DS 由来の icon を使う (design/CLAUDE.md
                              `.ecl-icon-btn--sm` = 「⋯ 等、 DS 由来の小型 icon-btn」/
                              16px)。 文字リテラルだとフォント依存で、 同じ画面の
                              ヘッダ側 ⋯ (2026-08-31 に icon 化済み) と point が
                              揃わない。 枠なし (ghost) なのは**テーブル行内のみ**の
                              例外で、 ヘッダ側は枠ありのまま。 */}
                            <FaEllipsisRegular boxSize={iconSize.gridInline} />
                          </IconButton>
                        </Tooltip>
                      </MenuTrigger>
                      <MenuContent>
                        {canManageGroup(row) ? (
                          <>
                            <MenuItem
                              disabled={onEditGroup === undefined}
                              onSelect={() => onEditGroup?.(row.groupId)}
                              value="edit"
                            >
                              {t('common:table.action.edit')}
                            </MenuItem>
                            <MenuItem
                              color="Text.negativeHigh"
                              disabled={onDeleteGroup === undefined}
                              onSelect={() => onDeleteGroup?.(row.groupId)}
                              value="delete"
                            >
                              {t('common:table.action.delete')}
                            </MenuItem>
                          </>
                        ) : (
                          <>
                            {canWithdrawRow && (
                              <MenuItem
                                onSelect={() => onWithdrawGroup?.(row.groupId)}
                                value="withdraw"
                              >
                                {t('page:biddingSpot.action.withdraw')}
                              </MenuItem>
                            )}
                            {canReturnRowToDraft && (
                              <MenuItem
                                onSelect={() =>
                                  onReturnGroupToDraft?.(row.groupId)
                                }
                                value="returnToDraft"
                              >
                                {t('page:biddingSpot.action.returnToDraft')}
                              </MenuItem>
                            )}
                            {onOpenGroup !== undefined &&
                              (isNonExpiredDraftBlockRow(row) ? (
                                <MenuItem
                                  onSelect={() => onOpenGroup(row.groupId)}
                                  value="edit"
                                >
                                  {t('common:table.action.edit')}
                                </MenuItem>
                              ) : (
                                <MenuItem
                                  onSelect={() => onOpenGroup(row.groupId)}
                                  value="detail"
                                >
                                  {t('common:action.detail')}
                                </MenuItem>
                              ))}
                          </>
                        )}
                      </MenuContent>
                    </MenuRoot>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
